/**
 * THE ADD-ON SHEET (provisional P112), which no artboard draws, opened from "Add to this visit" on
 * a planned or confirmed visit's card on Schedule, and drawn as the skip or move sheet is, in the
 * sheet `faithfully-kept-business/design/home/Schedule.dc.html` draws over Schedule (`Sheet.tsx`):
 * the visit's day and line, then what she would like added, the card's add-on lines as chips the
 * artboard draws for days, how many, the artboard's "Send the request", and under it that nothing
 * changes until we confirm and that the price is the card's, or, where any line offered is a
 * starting price, that the office confirms a starting price before anything is added, so a "from"
 * figure is never read as a price promised (ops contract 12.1, `ADD_TO_VISIT.startingPrice`).
 *
 * - THE CARD'S LINES, AND NOTHING OF HOME'S OWN. Each line in the card's own words with the card's
 *   price for one as ops pushed it, "from" where the card shows a starting price, never as exact
 *   (`addonChoice`). One line is chosen at a time; how many is 1 to 20, as ops takes `quantity`. No
 *   sum is drawn: ops prices an add-on from the card in force when the office agrees.
 * - ASKING IS ALL IT DOES (CLAUDE.md binding rule 1). It sends `visit.addon` with the visit, the
 *   line's code and how many through the request door, and a request the door has written closes the
 *   sheet and is shown on the card as sent to the office; an add-on shows on the visit only once ops
 *   pushes it agreed, which is Schedule's to draw.
 * - ONE REQUEST HOWEVER OFTEN IT IS TAPPED, as on the skip or move sheet: a key is minted once for
 *   each line and count, so a retry after a failure sends the same key and the door writes one row,
 *   and while one is out another tap does nothing. Both are held by Schedule for the visit
 *   (`lib/asks.ts`), never by the sheet.
 * - WHAT THE DOOR ANSWERS, NEVER SILENT: sent is Schedule's to show; a visit no longer open says so
 *   with the phone, and a line the card in force no longer holds (`422 invalid`) says the card has
 *   changed with the phone, each taking the focus and having Schedule read itself again, the second
 *   so she can choose again from the card as it now stands; a 404, the visit gone or not hers to
 *   ask about now, which the door answers alike, says what a visit no longer open says, in the same
 *   words, takes the focus and reads the place again, and the sheet stays open on it even where
 *   the read no longer holds the visit (Schedule keeps it drawn), never closing in silence; a visit
 *   that no longer offers an add-on, read again no longer open or gone, offers no line, no count
 *   and no Send, so the sheet keeps only what it says and its close button; a 401
 *   is the way to the door; an add-on already waiting reads the place again and closes the sheet,
 *   whose card then says where that one stands; past the door's own limits says so; anything else
 *   says it did not go through, with the phone, and leaves the sheet as it was to try again.
 * - A DIALOG, drawn in the sheet (`Sheet.tsx`), named by the visit's day, which takes the focus when
 *   it opens, the page behind it inert, and closed on Escape, its close button or a tap outside it.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { sendRequest, type CardLine, type ScheduledVisit, type TooMany } from '../api.ts';
import { keyFor, type Asking } from '../lib/asks.ts';
import { dayInWords } from '../lib/dates.ts';
import { visitLine } from '../lib/visits.ts';
import {
  ADD_TO_VISIT,
  addonChoice,
  FAILED_BEFORE_PHONE,
  NOT_OPEN_BEFORE_PHONE,
  SCHEDULE,
  tooManyBeforePhone,
} from '../words.ts';
import { NAVY_BUTTON } from './buttons.ts';
import { Check } from './MoveSheet.tsx';
import { Phone } from './Phone.tsx';
import { Sheet } from './Sheet.tsx';

interface AddonSheetProps {
  readonly visit: ScheduledVisit;
  /** The name of the visit's home, when she sees several homes (`homeNameOf`). */
  readonly home?: string | undefined;
  /** The business's day, from the place. */
  readonly today: string;
  /** The household the place shows, which the request is about. */
  readonly household: string;
  /** The card's add-on lines she may ask to add, as the place last read them. */
  readonly card: readonly CardLine[];
  /** Whether an add-on about the visit is out, and its keys, held by Schedule for the visit. */
  readonly asking: Asking;
  /** Closes the sheet, having asked nothing more. */
  readonly onClose: () => void;
  /** The door wrote the request, whose id this is. */
  readonly onSent: (id: string) => void;
  /** The session has ended. */
  readonly onSignedOut: () => void;
  /** What the place shows may be out of date: read it again. */
  readonly onStale: () => void;
}

/**
 * What the sheet says after an ask that was not sent: the visit can no longer be changed, the card
 * has changed, past the door's own limits, or a failure.
 */
type Problem = 'not_open' | 'card_changed' | 'too_many' | 'failed';

/** How many she may ask for, as ops takes `quantity`: a whole number from 1 to 20. */
const COUNTS: readonly number[] = Array.from({ length: 20 }, (_, index) => index + 1);

const LINE =
  'flex min-h-tap w-full items-center gap-1.5 rounded-control border-2 px-3 py-2 text-left ' +
  'text-small';
const LINE_CHOSEN = 'border-surface-brand bg-surface-brand-soft font-semibold text-text-body';
const LINE_FREE =
  'border-border-strong bg-surface-raised text-text-body hover:bg-surface-accent-soft';

export function AddonSheet({
  visit,
  home,
  today,
  household,
  card,
  asking,
  onClose,
  onSent,
  onSignedOut,
  onStale,
}: AddonSheetProps) {
  const questionId = useId();
  const howManyId = useId();
  const [chosen, setChosen] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [problem, setProblem] = useState<Problem | null>(null);
  /** Whose count of the door's own limits it was past, and when to try again, as the door said. */
  const [limit, setLimit] = useState<TooMany>();
  /** Whether this sheet is still open, and so has anything to say. */
  const mounted = useRef(true);
  /** What the sheet says after an ask that was not sent. */
  const problemLine = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // A visit no longer open, or a card read again without the line she chose, takes away what had the
  // focus, so the focus goes to what the sheet says instead, which stays, so it is read out and
  // stays in the sheet, where Escape still closes it.
  useEffect(() => {
    if (problem === 'not_open' || problem === 'card_changed') problemLine.current?.focus();
  }, [problem]);

  const line = visitLine(visit, today);
  /** The line she chose, while the card as last read still holds it. */
  const picked = card.find((each) => each.code === chosen);
  /** Whether any line offered is a starting price, which is never said as the price (12.1). */
  const startsFrom = card.some((each) => each.from === true);
  /**
   * Whether the visit, as the place now holds it, still offers an add-on: one read again no longer
   * open, or gone, which Schedule keeps drawn offering nothing, offers no line and no Send, and the
   * sheet keeps only what it says and its close button.
   */
  const canAdd = visit.offers.includes('visit.addon');

  async function send() {
    if (picked === undefined || asking.out) return;
    asking.out = true;
    setProblem(null);
    const answer = await sendRequest({
      key: keyFor(asking, `visit.addon ${picked.code} ${quantity}`),
      type: 'visit.addon',
      household,
      payload: { visit_id: visit.id, rate_line_code: picked.code, quantity },
    });
    asking.out = false;
    switch (answer.outcome) {
      case 'sent':
        asking.keys.clear();
        onSent(answer.id);
        return;
      case 'signed_out':
        onSignedOut();
        return;
      // An add-on of the visit already waits on the office: asking again would be refused again,
      // so the schedule is read again, and its card says where that one stands.
      case 'already_asked':
        onStale();
        if (mounted.current) onClose();
        return;
      // The visit is gone, or not hers to ask about now, which the door answers alike (binding rule
      // 2): said in the words of a visit no longer open, never silent, and the place read again.
      case 'not_found':
      case 'not_open':
        if (mounted.current) setProblem('not_open');
        onStale();
        return;
      case 'too_many':
        if (!mounted.current) return;
        setLimit(answer.limit);
        setProblem('too_many');
        return;
      case 'refused':
        if (answer.code === 'invalid') {
          if (mounted.current) setProblem('card_changed');
          onStale();
          return;
        }
        if (mounted.current) setProblem('failed');
        return;
      default:
        if (mounted.current) setProblem('failed');
    }
  }

  return (
    <Sheet
      heading={visit.date === undefined ? null : dayInWords(visit.date)}
      sub={
        home === undefined && !line ? undefined : (
          <>
            {home === undefined ? null : <p className="text-body text-text-body">{home}</p>}
            {line ? <p className="text-small text-balance text-text-muted">{line}</p> : null}
          </>
        )
      }
      onClose={onClose}
    >
      {canAdd ? (
        <>
          <p id={questionId} className="mt-5 text-body font-semibold text-text-body">
            {ADD_TO_VISIT.which}
          </p>
          <div role="group" aria-labelledby={questionId} className="mt-3 flex flex-col gap-2">
            {card.map((each) => {
              const on = each.code === chosen;
              return (
                <button
                  key={each.code}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setChosen(on ? null : each.code)}
                  className={`${LINE} ${on ? LINE_CHOSEN : LINE_FREE}`}
                >
                  {on ? <Check size={18} /> : null}
                  <span>{addonChoice(each.label, each.unitPriceCents, each.from === true)}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-5 flex flex-col gap-1">
            <label htmlFor={howManyId} className="text-body font-semibold text-text-body">
              {ADD_TO_VISIT.howMany}
            </label>
            <select
              id={howManyId}
              value={quantity}
              onChange={(event) => setQuantity(Number(event.target.value))}
              className="min-h-tap w-full cursor-pointer rounded-control border-2 border-border-strong bg-surface-raised px-3 text-body text-text-body"
            >
              {COUNTS.map((count) => (
                <option key={count} value={count}>
                  {count}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            disabled={picked === undefined}
            onClick={() => void send()}
            className={`mt-6 ${NAVY_BUTTON} disabled:cursor-not-allowed disabled:opacity-60`}
          >
            {SCHEDULE.send}
          </button>
        </>
      ) : null}

      <div aria-live="polite">
        {problem === null ? null : (
          <p ref={problemLine} tabIndex={-1} className="mt-4 text-body text-pretty">
            {problem === 'not_open'
              ? NOT_OPEN_BEFORE_PHONE
              : problem === 'card_changed'
                ? ADD_TO_VISIT.cardChangedBeforePhone
                : problem === 'too_many'
                  ? tooManyBeforePhone(limit)
                  : FAILED_BEFORE_PHONE}{' '}
            <Phone after="." />
          </p>
        )}
      </div>
      {canAdd ? (
        <p className="mt-3 text-small text-pretty text-text-muted">
          {ADD_TO_VISIT.nothingChanges}{' '}
          {startsFrom ? ADD_TO_VISIT.startingPrice : ADD_TO_VISIT.priceIsTheCards}
        </p>
      ) : null}
    </Sheet>
  );
}
